function hasExactDaysPassed(
  creationTimestamp: string | Date | null | undefined,
  targetDays: number,
  compareTimestamp?: string,
): boolean {
  if (!creationTimestamp || targetDays <= 0) {
    return false;
  }

  const creationDate = new Date(creationTimestamp);
  const compareDate = compareTimestamp ? new Date(compareTimestamp) : new Date();

  if (Number.isNaN(creationDate.getTime()) || Number.isNaN(compareDate.getTime())) {
    return false;
  }

  const targetMs = targetDays * 24 * 60 * 60 * 1000;

  return compareDate.getTime() - creationDate.getTime() >= targetMs;
}

import { useCallback, useContext, useEffect, useState } from 'react';
import { useCookies } from 'next-client-cookies';
import { AccountContext } from 'context/account';
import { getContextAwareCookieOptions, isEmbeddedBrowserContext } from 'helpers/cookies';
import { usePageLoader } from 'providers/loader';
import { useCart } from 'frontastic/hooks';

interface UseUserStatusProps {
  numberOfDays: number;
}

export const useUserStatus = ({ numberOfDays }: UseUserStatusProps) => {
  const { account, accountLoaded } = useContext(AccountContext);
  const [isNewUser, setIsNewUser] = useState<boolean | null>(null);
  const cookies = useCookies();
  const { showLoader, hideLoader, isLoading } = usePageLoader();
  const { orderHistory } = useCart();
  const fetchHasOrders = useCallback(async (): Promise<boolean> => {
    if (!orderHistory) {
      return false;
    }

    try {
      const orders = await orderHistory(1);
      const hasOrders = orders.length > 0;

      cookies.set(
        'hasPastOrders',
        String(hasOrders),
        getContextAwareCookieOptions({
          isEmbedded: isEmbeddedBrowserContext(),
        }),
      );

      return hasOrders;
    } catch {
      return false;
    }
  }, [orderHistory]);

  useEffect(() => {
    let isMounted = true;

    const checkUserStatus = async () => {
      if (!accountLoaded) {
        return;
      }

      if (!account?.accountId) {
        cookies.set(
          'hasPastOrders',
          'false',
          getContextAwareCookieOptions({
            isEmbedded: isEmbeddedBrowserContext(),
          }),
        );
        setIsNewUser(true);
        return;
      }

      const isOlderThanThreshold = hasExactDaysPassed(account?.creationTimestamp, numberOfDays);
      // Fast path: old account => not a new user
      if (isOlderThanThreshold) {
        setIsNewUser(false);
        return;
      }
      showLoader();
      try {
        const hasOrders = await fetchHasOrders();
        if (isMounted) {
          setIsNewUser(!hasOrders);
        }
      } finally {
        hideLoader();
      }
    };
    checkUserStatus();
    return () => {
      isMounted = false;
    };
  }, [accountLoaded, account?.accountId, account?.creationTimestamp, numberOfDays, fetchHasOrders]);

  return {
    loading: isLoading, // This is true when order history is being checked
    isNewUser,
  };
};
